import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Card, Empty, message, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useWindowWidth } from '@react-hook/window-size';
import { serverFetch } from '../../server';
import { cartRemove, cartClear } from '../../redux/actions';
import { closePaymentWindow, paySale, preparePaymentWindow, shouldOpenPaymentWindow } from '../../extensions/payment-window';
import { PayButtons } from '../../controls/index.js';
import PaymentTerms from '../catalog/payment-terms';
import Auth from '../auth/auth.js';
import GuestContact from '../auth/guest-contact.js';
import { companyAuthMode } from '../../extensions/helper';
import './cart.css';

const Cart = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const user = useSelector((s) => s.user);
    const company = useSelector((s) => s.company);
    const allItems = useSelector((s) => s.cart?.items || []);
    const onlyWidth = useWindowWidth();
    const [cond1, setCond1] = useState(false);
    const [cond2, setCond2] = useState(false);
    const [paying, setPaying] = useState(false);
    const [showSignin, setShowSignin] = useState(false);
    const [showGuest, setShowGuest] = useState(false);
    const [guestUser, setGuestUser] = useState(null);
    const [pendingPaymentSystem, setPendingPaymentSystem] = useState(null);
    const paymentWindowRef = useRef(null);

    const items = allItems.filter((i) => i.companyId === company?.id);
    const totalPrice = items.reduce((sum, i) => sum + (Number(i.totalPrice) || 0), 0);

    const checkout = (system, actor = user) => {
        setPaying(true);
        const basket = {
            totalPrice,
            items: items.map((i) => ({ sale: i.sale, rents: i.rents || [] })),
        };
        serverFetch('sale/basket', { method: 'POST', bodyData: basket }, actor)
            .then((data) => {
                dispatch(cartClear());
                return paySale(actor, data.id, system, false, paymentWindowRef);
            })
            .catch((e) => {
                closePaymentWindow(paymentWindowRef.current);
                paymentWindowRef.current = null;
                e.UserMessage && message.error(e.UserMessage);
            })
            .finally(() => setPaying(false));
    };

    // Продолжение оплаты после авторизации
    useEffect(() => {
        if (user && pendingPaymentSystem && !showSignin) {
            const system = pendingPaymentSystem;
            setPendingPaymentSystem(null);
            checkout(system);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, pendingPaymentSystem, showSignin]);

    const onPay = (system) => {
        const paymentWindow = preparePaymentWindow(system);
        if (shouldOpenPaymentWindow(system) && !paymentWindow) {
            message.error('Разрешите всплывающие окна для перехода к оплате');
            return;
        }
        paymentWindowRef.current = paymentWindow;
        if (user) {
            checkout(system);
            return;
        }
        if (guestUser) {
            checkout(system, guestUser);
            return;
        }
        setPendingPaymentSystem(system);
        if (company?.authMode === companyAuthMode.NoAuth) setShowGuest(true);
        else setShowSignin(true);
    };

    const onGuestSuccess = (session) => {
        setGuestUser(session);
        setShowGuest(false);
        if (pendingPaymentSystem) {
            const system = pendingPaymentSystem;
            setPendingPaymentSystem(null);
            checkout(system, session);
        }
    };

    const onGuestClose = () => {
        setShowGuest(false);
        if (!user) {
            setPendingPaymentSystem(null);
            closePaymentWindow(paymentWindowRef.current);
            paymentWindowRef.current = null;
        }
    };

    const handleAuthClose = () => {
        setShowSignin(false);
        if (!user) {
            setPendingPaymentSystem(null);
            closePaymentWindow(paymentWindowRef.current);
            paymentWindowRef.current = null;
        }
    };

    if (items.length === 0) {
        return (
            <div className='block-content cart-page'>
                <Empty description='Корзина пуста'>
                    <Button type='primary' onClick={() => history.push('/catalog')}>Перейти в каталог</Button>
                </Empty>
            </div>
        );
    }

    const disablePayment = paying || !company || !cond1 || !cond2 || totalPrice <= 0;

    return (
        <div className='block-content cart-page'>
            <div className='cart-header'>
                <h2 className='cart-title'>Корзина</h2>
                <Popconfirm title='Очистить корзину?' okText='Да' cancelText='Нет' onConfirm={() => dispatch(cartClear())}>
                    <Button type='link' danger>Очистить</Button>
                </Popconfirm>
            </div>
            <div className='cart-items'>
                {items.map((i) => (
                    <Card key={i.key} className='cart-item' bordered={false}>
                        <div className='cart-item-top'>
                            <div className='cart-item-name'>{i.name}</div>
                            <div className='cart-item-price'>{Number(i.totalPrice).toLocaleString()} ₽</div>
                        </div>
                        {(i.details || []).map((d, idx) => (
                            <div key={idx} className='cart-item-meta'>{d}</div>
                        ))}
                        <div className='cart-item-actions'>
                            <Button type='text' danger size='small' icon={<DeleteOutlined />} onClick={() => dispatch(cartRemove(i.key))}>
                                Удалить
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
            <div className='cart-footer'>
                <div className='cart-total'>
                    <span className='cart-total-label'>ИТОГО</span>
                    <span className='cart-total-value'>{totalPrice.toLocaleString()} ₽</span>
                </div>
                <PaymentTerms company={company} autopay={false} cond1={cond1} cond2={cond2} setCond1={setCond1} setCond2={setCond2} />
                {process.env.REACT_APP_BLOCK_CARD && <div style={{ color: 'red' }}>Оплата картами временно ограничена</div>}
                {company && (
                    <PayButtons
                        company={company}
                        isMobile={onlyWidth < 700}
                        onPay={onPay}
                        disablePayment={disablePayment}
                        autopay={false}
                        price={totalPrice}
                        onBack={() => history.push('/catalog')}
                    />
                )}
            </div>
            {showSignin && <Auth onClose={handleAuthClose} />}
            {showGuest && <GuestContact onClose={onGuestClose} onSuccess={onGuestSuccess} />}
        </div>
    );
};

export default Cart;
